import { useState } from "react";
import {
  CashMovementKindLabel,
  CashMovementMethod,
  PaymentMethodLabel,
  type CashMovement,
  type CashMovementKind,
} from "@probetalab/contracts";
import { get, run } from "../lib/api.js";
import { fmtTime, money, parseMoney } from "../lib/format.js";
import { useBranch, useSession } from "../lib/session.js";
import { useToast } from "../lib/toast.js";
import { errorMessage, useQuery } from "../lib/useQuery.js";
import { Alert, Button, Card, Field, Input, Money, Segmented, Select, Sheet, Textarea } from "./ui.js";
import { NO_AUTOFILL } from "../lib/noAutofill.js";

const MONEY_METHODS = CashMovementMethod.options;

/**
 * «Nuevo movimiento» of the corte de caja (pilot request 2026-11-03, modelled on Toronja's
 * «Agregar movimiento»): an extra ingreso or egreso in the drawer. It is registered in the acting
 * user's own drawer (who registers is who answers for that cash, so there is no «Vendedor» to
 * pick) and enters the close of that drawer. Never deleted; voided with a reason by a Químico or
 * above, only before the close that includes it.
 */
export function CashMovementsCard({
  movements,
  canAdd,
  addHint,
  onChanged,
}: {
  movements: CashMovement[];
  /** The drawer shown is the acting user's, for today. */
  canAdd: boolean;
  addHint?: string;
  onChanged: () => void;
}) {
  const { can } = useSession();
  const [adding, setAdding] = useState(false);
  const [voiding, setVoiding] = useState<CashMovement | null>(null);
  return (
    <>
      <Card
        title="Movimientos de caja"
        pad={false}
        actions={
          <Button size="sm" variant="primary" icon="plus" disabled={!canAdd} onClick={() => setAdding(true)}>
            Nuevo movimiento
          </Button>
        }
      >
        {!canAdd && addHint && <div className="list-row faint small">{addHint}</div>}
        {movements.length === 0 ? (
          <div className="list-row faint">Sin ingresos ni egresos extra en el periodo.</div>
        ) : (
          <div className="table-scroll">
            <table className="table compact">
              <thead>
                <tr>
                  <th>Hora</th>
                  <th>Concepto</th>
                  <th>Forma de pago</th>
                  <th className="right">Monto</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {movements.map((m) => {
                  const gone = !!m.voided_at;
                  return (
                    <tr key={m.id}>
                      <td className="small nowrap">{fmtTime(m.occurred_at)}</td>
                      <td style={gone ? { textDecoration: "line-through" } : undefined}>
                        <span style={{ fontWeight: 500 }}>{m.concept}</span>
                        {m.note && <div className="small faint">{m.note}</div>}
                        {gone && <div className="small faint" style={{ textDecoration: "none" }}>Anulado: {m.void_reason}</div>}
                      </td>
                      <td className="small">{PaymentMethodLabel[m.method]}</td>
                      <td className="right tnum" style={{ fontWeight: 600, color: gone ? "var(--ink-3)" : m.kind === "egreso" ? "var(--red)" : "var(--brand)", textDecoration: gone ? "line-through" : undefined }}>
                        {m.kind === "egreso" ? "−" : "+"} {money(m.amount_cents)}
                      </td>
                      <td className="right">
                        {!gone && !m.cash_close_id && can("quimico") && (
                          <Button size="sm" variant="ghost" onClick={() => setVoiding(m)} aria-label={`Anular movimiento ${m.concept}`}>
                            Anular
                          </Button>
                        )}
                        {!gone && m.cash_close_id && <span className="small faint">En corte</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      <NewMovementSheet open={adding} onClose={() => setAdding(false)} onSaved={onChanged} />
      <VoidMovementSheet target={voiding} onClose={() => setVoiding(null)} onDone={onChanged} />
    </>
  );
}

function NewMovementSheet({ open, onClose, onSaved }: { open: boolean; onClose: () => void; onSaved: () => void }) {
  const branch = useBranch();
  const { me } = useSession();
  const toast = useToast();
  const [kind, setKind] = useState<CashMovementKind>("ingreso");
  const [concept, setConcept] = useState("");
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState<CashMovementMethod>("efectivo");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const suggestions = useQuery(() => get("cashMovementConcepts", { query: { kind } }), [kind], { enabled: open });
  const cents = parseMoney(amount);
  const valid = concept.trim().length > 0 && cents !== null && cents > 0;

  function reset() {
    setKind("ingreso");
    setConcept("");
    setAmount("");
    setMethod("efectivo");
    setNote("");
  }

  async function save() {
    if (!valid || cents === null) return;
    setBusy(true);
    try {
      await run<"cash_movement.record", CashMovement>(
        "cash_movement.record",
        { branch_id: branch.id, kind, concept: concept.trim(), amount_cents: cents, method, note: note.trim() || null },
        { branch_id: branch.id },
      );
      toast.success(kind === "ingreso" ? "Ingreso registrado." : "Egreso registrado.");
      reset();
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Nuevo movimiento"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" icon="check" busy={busy} disabled={!valid} onClick={() => void save()}>
            Guardar movimiento
          </Button>
        </>
      }
    >
      <div className="stack-lg">
        <Field label="Tipo de movimiento">
          <Segmented<CashMovementKind>
            block
            value={kind}
            onChange={setKind}
            options={[
              { value: "ingreso", label: CashMovementKindLabel.ingreso },
              { value: "egreso", label: CashMovementKindLabel.egreso },
            ]}
          />
        </Field>
        <Field label="Concepto" hint="Escribe uno nuevo o elige uno anterior.">
          <>
            <Input list="cash-movement-concepts" value={concept} onChange={(e) => setConcept(e.target.value)} maxLength={120} placeholder={kind === "ingreso" ? "Ej. Fondo de caja" : "Ej. Garrafón de agua"} autoFocus />
            <datalist id="cash-movement-concepts">
              {(suggestions.data?.items ?? []).map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </>
        </Field>
        <Field label="Monto">
          <div className="input-wrap">
            <span className="suffix">MXN</span>
            <Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className="lg" />
          </div>
        </Field>
        <Field label="Forma de pago">
          <Select value={method} onChange={(e) => setMethod(e.target.value as CashMovementMethod)} {...NO_AUTOFILL}>
            {MONEY_METHODS.map((m) => (
              <option key={m} value={m}>
                {PaymentMethodLabel[m]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Nota" optional>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={200} />
        </Field>
        <p className="small muted">Se registra en tu caja como {me?.user.full_name ?? "tu usuario"} y entra en tu próximo corte.</p>
      </div>
    </Sheet>
  );
}

function VoidMovementSheet({ target, onClose, onDone }: { target: CashMovement | null; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [shown, setShown] = useState<string | null>(null);
  if (target && shown !== target.id) {
    setShown(target.id);
    setReason("");
  }
  return (
    <Sheet
      open={!!target}
      onClose={onClose}
      title="Anular movimiento"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Conservar
          </Button>
          <Button
            variant="danger"
            icon="x"
            busy={busy}
            disabled={reason.trim().length < 3}
            onClick={async () => {
              if (!target) return;
              setBusy(true);
              try {
                await run<"cash_movement.void", CashMovement>("cash_movement.void", { movement_id: target.id, reason: reason.trim() });
                toast.success("Movimiento anulado.");
                onDone();
                onClose();
              } catch (e) {
                toast.error(errorMessage(e));
              } finally {
                setBusy(false);
              }
            }}
          >
            Anular movimiento
          </Button>
        </>
      }
    >
      {target && (
        <>
          <Alert tone="amber">
            El {CashMovementKindLabel[target.kind].toLowerCase()} de <b>{money(target.amount_cents)}</b> ({target.concept}, {PaymentMethodLabel[target.method]}) queda tachado y deja de contar en el corte. No se elimina: queda en la bitácora con tu nombre y el motivo.
          </Alert>
          <Field label="Motivo">
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Por qué se anula" autoFocus />
          </Field>
        </>
      )}
    </Sheet>
  );
}

/** Totals of the movements and the net per payment method, shown under the cobros when there are movements. */
export function NetByMethod({ s }: { s: import("@probetalab/contracts").CashCloseSummary }) {
  if (s.movements_count === 0) return null;
  return (
    <div className="table-scroll" style={{ borderTop: "1px solid var(--line-soft)" }}>
      <table className="table">
        <thead>
          <tr>
            <th>Neto por forma de pago</th>
            <th className="right">Cobros + ingresos − egresos</th>
          </tr>
        </thead>
        <tbody>
          {s.net_by_method.map((m) => (
            <tr key={m.method}>
              <td style={{ fontWeight: 500 }}>{PaymentMethodLabel[m.method]}</td>
              <td className="right">
                <Money cents={m.amount_cents} />
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td style={{ fontWeight: 600 }}>Total neto</td>
            <td className="right" style={{ fontWeight: 600 }}>
              <Money cents={s.net_total_cents} />
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
